import { useState } from "react";
import { Tooltip, Text } from "../"
import { ExclamationCircleOutlined } from "@ant-design/icons";
import "./boxItem.css";

/**
 * Universal BoxItem Component
 * Combines standard UI templates (image, title, extras) with advanced D&D support.
 */
const BoxItem = (props) => {
  const {
    id,
    key,
    title,
    tooltipTitle,
    tooltipPlacement = "top",
    showTooltip = true,
    isPopoverContentVisible = false,
    error,
    extraRight,
    extraLeft,
    className = "",
    divClassName = "col-lg-4 col-sm-6 col-xs-12",
    style = {},
    onClick,
    onDoubleClick,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    draggable,
    dragEffectAllowed = "move",
    columnType,
    onColumnMap,
    isDisabledColumn,
    isReadOnlyAndCustomDashboardMode,
    children,
    ...rest
  } = props;

  const [isTooltipVisible, setIsTooltipVisible] = useState(true);

  // Hides tooltip when click on BoxItem
  const handleTooltipHide = () => {
    setIsTooltipVisible(false);
  };

  // Shows tooltip when hover on BoxItem
  const handleTooltipShow = () => {
    setIsTooltipVisible(true);
  };

  /**
   * Advanced Drag Start Handler (Migrated from BoxItem2)
   * Creates a ghost element for smooth dragging experience.
   */
  const handleDragStart = (event) => {
    if (!draggable) return;

    let div = document.getElementById(id);
    if (div) {
      let temp = document.createElement("div");
      temp.id = "dragged-" + id;
      temp.style.position = "fixed";
      temp.style.top = "-100dvh";
      temp.style.left = "-100dvw";
      temp.style.backgroundColor = "white";
      temp.style.opacity = "1";
      temp.style.width = div.offsetWidth + "px";
      temp.style.zIndex = "10000";

      let clone = div.cloneNode(true);
      clone.style.opacity = "1";
      clone.style.margin = "0";
      clone.style.boxShadow = "none";

      temp.appendChild(clone);
      document.body.appendChild(temp);

      event.dataTransfer.setDragImage(temp, 0, 0);
      event.dataTransfer.effectAllowed = dragEffectAllowed;
    }

    if (typeof onDragStart === "function") {
      onDragStart(event);
    }
  };

  /**
   * Drag End Handler
   */
  const handleDragEnd = (event) => {
    const temp = document.getElementById("dragged-" + id);
    if (temp) {
      temp.remove();
    }
    event.dataTransfer.clearData();
    if (typeof onDragEnd === "function") {
      onDragEnd(event);
    }
  };

  let colStyle = onColumnMap === "onColumnMap"
    ? isDisabledColumn === true
      ? { ...style, backgroundColor: "#d5e8f780" }
      : { ...style, backgroundColor: "#d5e8f7" }
    : { ...style };

  const combinedClassName = `box-item ${className} ${columnType === "newColumn" ? " newColumn" : ""}`.trim();

  return (
    <div
      id={id}
      key={key}
      className={divClassName === "noStyle" ? "" : `box-item-component ${divClassName}`}
      onClick={(e) => {
        handleTooltipHide();
        if (onClick) onClick(e);
      }}
      onMouseEnter={handleTooltipShow}
      onDoubleClick={onDoubleClick}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      {...rest}
    >
      <Tooltip
        tooltipPlacement={tooltipPlacement}
        tooltip={isTooltipVisible && showTooltip !== false && !isPopoverContentVisible && (
          <>
            <div>{tooltipTitle || title}</div>
            {error && <div style={{ color: "#ff4d4f", fontSize: "11px" }}>{error}</div>}
          </>
        )}
        overlayClassName="box-item-tooltip"
      >
        <div className={props.contentItem ? "box-item-content" : ""}>
          <span
            id={id !== undefined ? id + " color" : title}
            style={colStyle}
            className={combinedClassName}
          >
            {
              (extraLeft || extraRight) &&
              <div className="box-item-extra left" onClick={(e) => e.stopPropagation()}>
                {!isReadOnlyAndCustomDashboardMode && extraLeft}
              </div>
            }

            <Text
              className={onColumnMap === true ? "box-text onColumnMap Active" : "box-text"}
            >
              {error && (
                <ExclamationCircleOutlined
                  style={{ marginRight: 6, color: "#ff4d4f" }}
                />
              )}
              {children || title}
            </Text>

            {
              (extraLeft || extraRight) &&
              <div className="box-item-extra right" onClick={(e) => e.stopPropagation()}>
                {!isReadOnlyAndCustomDashboardMode && extraRight && extraRight}
              </div>
            }
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

export default BoxItem;